import { apiClient } from '../config/api';

// FEAT-056 — Customizable Care Reports (MVP-18.34). Client-side service for the
// read-only care-reports API. The catalog + report payloads are role-aware: the
// API scopes both the selectable fields and the underlying visit data to what
// the authenticated caller may see (caregivers → visits they authored,
// careseekers → visits for their bookings, admins → everything). Endpoints are
// namespaced under `/care-reports` (NOT `/reports`, which FEAT-041 owns).

export type CareReportFieldKey =
  | 'visitDate'
  | 'visitStartAt'
  | 'visitEndAt'
  | 'totalHours'
  | 'mileage'
  | 'adlSummary'
  | 'careNotes'
  | 'billingClassification';

export type CareReportRole = 'CAREGIVER' | 'PATIENT' | 'ADMIN';

export type CareReportGroupBy =
  | 'none'
  | 'day'
  | 'week'
  | 'month'
  | 'billingClassification';

export interface CareReportFieldDef {
  key: CareReportFieldKey;
  label: string;
  description?: string;
  roles: Array<'CAREGIVER' | 'PATIENT'>;
}

export interface CareReportAdlSummary {
  done: number;
  notDone: number;
  notApplicable: number;
  total: number;
}

/** A report row contains ONLY the selected field keys. */
export type CareReportRow = Record<string, unknown>;

export interface CareReportSummary {
  visitCount: number;
  totalHours: number;
  totalMileage: number;
  adl: CareReportAdlSummary;
}

export interface CareReportGroup {
  key: string;
  label: string;
  visitCount: number;
  totalHours: number;
  totalMileage: number;
  rows: CareReportRow[];
}

export interface CareReportFieldCatalog {
  role: CareReportRole;
  fields: CareReportFieldDef[];
}

export interface CareReport {
  generatedAt: string;
  role: CareReportRole;
  dateFrom: string;
  dateTo: string;
  fields: CareReportFieldDef[];
  groupBy: CareReportGroupBy;
  visitCount: number;
  summary: CareReportSummary;
  rows: CareReportRow[];
  groups: CareReportGroup[] | null;
}

export interface GenerateReportParams {
  fields: string[];
  dateFrom: string;
  dateTo: string;
  groupBy?: CareReportGroupBy;
}

interface FieldCatalogResponse {
  data: CareReportFieldCatalog;
}

interface CareReportResponse {
  data: CareReport;
}

class CareReportsService {
  /** Fetch the role-aware catalog of selectable report fields. */
  async getReportFields(): Promise<CareReportFieldCatalog> {
    const response = await apiClient.get<FieldCatalogResponse>('/care-reports/fields');
    return response.data.data;
  }

  /** Generate a customizable report over the caller's own visit data. */
  async generateReport(params: GenerateReportParams): Promise<CareReport> {
    const response = await apiClient.post<CareReportResponse>('/care-reports', {
      groupBy: 'none',
      ...params,
    });
    return response.data.data;
  }
}

export const careReportsService = new CareReportsService();
